// src/pages/Forecast.jsx
import React, { useEffect, useState, useMemo } from "react";
import { fmt, PRICES } from "../config/devices";
import { FilterBtn } from "../components/UI";

const FORECAST_CAMPUSES = ["CCK", "CCW"];
const ADD_TO_BUDGET = "add-to-budget";
const FORECAST_REVIEW_SESSION = {
  key: "",
  states: {},
};

function emptyReviewState() {
  return { devices: {}, miscItems: [], finalizedAt: null, finalizedBy: null, finalizedCampuses: {}, jiraTickets: [] };
}

function campusFinalization(state, campus, devices) {
  if (!state) return null;
  const direct = state.finalizedCampuses?.[campus];
  if (direct?.finalizedAt) return direct;

  const legacyTicket = (state.jiraTickets || []).find(ticket => ticket.campus === campus);
  if (state.finalizedAt && legacyTicket) {
    return {
      finalizedAt: state.finalizedAt,
      finalizedBy: state.finalizedBy,
      jiraKey: legacyTicket.key,
      jiraTickets: [legacyTicket],
    };
  }

  const hasReviewedDevices = devices.length > 0 && devices.every(d => {
    const status = state.devices?.[d.serial]?.status || "unreviewed";
    return status !== "unreviewed";
  });

  return state.finalizedAt && hasReviewedDevices
    ? { finalizedAt: state.finalizedAt, finalizedBy: state.finalizedBy, jiraKey: state.jiraKey, jiraTickets: state.jiraTickets || [] }
    : null;
}

export default function Forecast({ computeDevices, priceMap, devicePrice, lifecycleYears = 4, reviewProgram = "4-year" }) {
  const prices = priceMap || PRICES;
  const [campusFilter, setCampusFilter] = useState("All");
  const [copyStatus, setCopyStatus] = useState("");
  const [reviewStates, setReviewStates] = useState(FORECAST_REVIEW_SESSION.key.startsWith(reviewProgram + "|") ? FORECAST_REVIEW_SESSION.states : {});
  const [reviewLoading, setReviewLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  const filtered = useMemo(() =>
    campusFilter === "All" ? computeDevices : computeDevices.filter(d => d.campus === campusFilter),
    [computeDevices, campusFilter]
  );

  const years = useMemo(() => {
    const min = currentYear + 1;
    const max = lifecycleYears === 5 ? currentYear + 5 : currentYear + 4;
    const all = []; for (let y = min; y <= max; y++) all.push(y); return all;
  }, [computeDevices, currentYear, lifecycleYears]);
  useEffect(() => {
    let cancelled = false;
    const yearsToLoad = years.filter(y => y >= currentYear).slice(0, 8);
    if (!yearsToLoad.length) return undefined;
    const cacheKey = [reviewProgram, ...yearsToLoad].join("|");

    if (FORECAST_REVIEW_SESSION.key === cacheKey) {
      setReviewStates(FORECAST_REVIEW_SESSION.states);
      setReviewLoading(false);
      return undefined;
    }

    setReviewStates({});
    setReviewLoading(true);
    Promise.all(yearsToLoad.map(async year => {
      try {
        const res = await fetch(`/api/budget-review?year=${encodeURIComponent(year)}&program=${encodeURIComponent(reviewProgram)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return [year, { ...emptyReviewState(), ...(data.state || {}) }];
      } catch {
        return [year, emptyReviewState()];
      }
    }))
      .then(entries => {
        if (cancelled) return;
        const states = Object.fromEntries(entries);
        FORECAST_REVIEW_SESSION.key = cacheKey;
        FORECAST_REVIEW_SESSION.states = states;
        setReviewStates(states);
      })
      .finally(() => {
        if (!cancelled) setReviewLoading(false);
      });

    return () => { cancelled = true; };
  }, [years, currentYear, reviewProgram]);

  const yearData = useMemo(() =>
    years.map(y => {
      const devs = filtered.filter(d => Number(d.eolYear) === Number(y));
      const state = reviewStates[y] || emptyReviewState();
      const selectedCampuses = campusFilter === "All" ? FORECAST_CAMPUSES : [campusFilter];
      const reviewedCampusData = selectedCampuses.map(campus => {
        const campusDevices = devs.filter(d => d.campus === campus);
        const finalization = campusFinalization(state, campus, campusDevices);
        if (!finalization) {
          return { campus, finalized: false, projected: campusDevices.length, reviewed: null, budget: null };
        }
        const manuallyAddedDevices = filtered.filter(d =>
          d.campus === campus &&
          (state.devices?.[d.serial]?.status || "unreviewed") === ADD_TO_BUDGET &&
          !campusDevices.some(device => device.serial === d.serial)
        );
        const miscItems = (state.miscItems || []).filter(item =>
          !item.removed &&
          Number(item.year) === Number(y) &&
          item.campus === campus
        );
        const reviewedDevices = [
          ...campusDevices.filter(d => (state.devices?.[d.serial]?.status || "unreviewed") !== "excluded"),
          ...manuallyAddedDevices,
        ];
        return {
          campus,
          finalized: true,
          projected: campusDevices.length,
          reviewed: reviewedDevices.length + miscItems.length,
          budget: reviewedDevices.reduce((sum, d) => sum + (prices[d.deviceType] || 0), 0) + miscItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
        };
      });
      const reviewedCampuses = reviewedCampusData.filter(c => c.finalized);
      const reviewedBudget = reviewedCampuses.reduce((sum, c) => sum + (c.budget || 0), 0);
      const reviewedTotal = reviewedCampuses.reduce((sum, c) => sum + (c.reviewed || 0), 0);
      const status = reviewedCampuses.length === 0
        ? "Forecast"
        : reviewedCampuses.length === selectedCampuses.length
          ? "ICT reviewed"
          : "Partially reviewed";
      return {
        year: y,
        total: devs.length,
        cck: devs.filter(d => d.campus === "CCK").length,
        ccw: devs.filter(d => d.campus === "CCW").length,
        budget: devs.reduce((s, d) => s + (prices[d.deviceType] || 0), 0),
        reviewedBudget,
        reviewedTotal,
        reviewedCampusData,
        status,
      };
    }), [filtered, years, prices, reviewStates, campusFilter]);

  const totals = useMemo(() => ({
    total:  filtered.length,
    cck:    filtered.filter(d => d.campus === "CCK").length,
    ccw:    filtered.filter(d => d.campus === "CCW").length,
    budget: filtered.reduce((s, d) => s + (prices[d.deviceType] || 0), 0),
    reviewedBudget: yearData.reduce((sum, y) => sum + y.reviewedBudget, 0),
    reviewedTotal: yearData.reduce((sum, y) => sum + y.reviewedTotal, 0),
  }), [filtered, prices, yearData]);

  const maxBudget = Math.max(...yearData.map(y => y.budget), 1);
  const maxReviewedBudget = Math.max(...yearData.map(y => y.reviewedBudget || 0), 1);
  const chartMax = Math.max(maxBudget, maxReviewedBudget, 1);

  const copyLeadershipLink = async () => {
    const url = `${window.location.origin}/leadership-forecast?cycle=${lifecycleYears}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus("Leadership link copied");
    } catch {
      setCopyStatus(url);
    }
    window.setTimeout(() => setCopyStatus(""), 3000);
  };

  if (!computeDevices.length) {
    return (
      <div className="fade-up" style={{ textAlign: "center", padding: "80px 0", color: "var(--muted)" }}>
        <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.3 }}>◷</div>
        <p>Upload a CSV from the Dashboard first.</p>
      </div>
    );
  }

  return (
    <div className="fade-up">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 4 }}>{lifecycleYears}-Year Budget Forecast</h1>
          <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 4 }}>{lifecycleYears === 5 ? "Ratified five-year lifecycle; the completed 2026 refresh is excluded and remaining devices use LWT Date Ordered + 5 years." : "Archived four-year planning from 2027; the completed 2026 refresh is excluded."} Finalised Budget Review figures are shown where available.</p>
          <p style={{ color: "var(--muted)", fontSize: 12 }}>Cost basis: <span style={{ color: "var(--warn)", fontWeight: 600 }}>{fmt(devicePrice ?? 1704)}</span> per device</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {copyStatus && <span style={{ color: copyStatus.startsWith("http") ? "var(--muted)" : "var(--success)", fontSize: 12, fontWeight: 650 }}>{copyStatus}</span>}
          <button onClick={copyLeadershipLink} style={{ background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--text)", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
            Copy Leadership Link
          </button>
        </div>
      </div>

      {/* Campus filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {["All", "CCK", "CCW"].map(c => (
          <FilterBtn key={c} label={c} active={campusFilter === c} onClick={() => setCampusFilter(c)} color={c === "CCK" ? "var(--cck)" : c === "CCW" ? "var(--ccw)" : undefined} />
        ))}
        {reviewLoading && <span style={{ alignSelf: "center", marginLeft: 8, color: "var(--success)", fontSize: 12, fontWeight: 650 }}>Checking finalised reviews…</span>}
      </div>

      {/* Visual bar chart */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 28, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Annual budget forecast</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Projected vs ICT reviewed</div>
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "var(--muted)" }}>
            <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "var(--accent)", marginRight: 6 }} />Projected</span>
            <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "var(--success)", marginRight: 6 }} />ICT reviewed</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 190, paddingTop: 6 }}>
          {yearData.filter(y => y.year >= currentYear).map(y => {
            const h = chartMax > 0 ? Math.max(Math.round((y.budget / chartMax) * 120), y.budget > 0 ? 4 : 0) : 0;
            const reviewedH = chartMax > 0 ? Math.max(Math.round((y.reviewedBudget / chartMax) * 120), y.reviewedBudget > 0 ? 4 : 0) : 0;
            const isCurrent = y.year === currentYear;
            return (
              <div key={y.year} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ height: 30, maxWidth: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 4, fontSize: 10, lineHeight: 1.15, fontFamily: "var(--font-mono)", textAlign: "center", overflow: "hidden", overflowWrap: "anywhere" }}>
                  {y.budget ? (
                    <>
                      <span style={{ color: isCurrent ? "var(--warn)" : "var(--accent)" }}>{fmt(y.budget)}</span>
                      {y.reviewedBudget ? (
                        <>
                          <span style={{ color: "var(--muted)" }}>/</span>
                          <span style={{ color: "var(--success)" }}>{fmt(y.reviewedBudget)}</span>
                        </>
                      ) : null}
                    </>
                  ) : "—"}
                </div>
                <div style={{ width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 4 }}>
                  <div title="Projected" style={{ flex: "1 1 0", maxWidth: 34, height: h, background: isCurrent ? "var(--warn)" : "rgba(79,156,249,0.4)", borderRadius: "4px 4px 0 0", minHeight: y.budget > 0 ? 4 : 0, transition: "height 0.5s" }} />
                  <div title="ICT reviewed" style={{ flex: "1 1 0", maxWidth: 34, height: reviewedH, background: y.reviewedBudget ? "rgba(34,197,94,0.75)" : "rgba(255,255,255,0.06)", borderRadius: "4px 4px 0 0", minHeight: y.reviewedBudget > 0 ? 4 : 0, transition: "height 0.5s" }} />
                </div>
                <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: isCurrent ? "var(--warn)" : "var(--muted)", fontWeight: isCurrent ? 700 : 400 }}>{y.year}</div>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{y.reviewedTotal ? `${y.total}/${y.reviewedTotal}` : y.total || 0} devices</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--bg3)", borderBottom: "1px solid var(--border)" }}>
              {["Year", "Projected", "ICT Reviewed", "Difference", "Campus Split", "Status"].map((h, i) => (
                <th key={h} style={{ padding: "12px 16px", fontSize: 10, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", textAlign: i > 0 ? "right" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {yearData.map((y, i) => {
              const isCurrent = y.year === currentYear;
              const isPast = y.year < currentYear;
              const diff = y.status === "ICT reviewed" ? y.reviewedBudget - y.budget : null;
              const statusColor = y.status === "ICT reviewed" ? "var(--success)" : y.status === "Partially reviewed" ? "var(--warn)" : "var(--muted)";
              return (
                <tr key={y.year} style={{ borderBottom: "1px solid var(--border)", background: isCurrent ? "rgba(245,158,11,0.04)" : i%2===0?"transparent":"rgba(255,255,255,0.01)", opacity: isPast ? 0.4 : 1 }}>
                  <td style={{ padding: "12px 16px", fontWeight: isCurrent ? 700 : 500, color: isCurrent ? "var(--warn)" : "var(--text)", fontSize: 14, fontFamily: "var(--font-mono)" }}>
                    {y.year}
                    {isCurrent && <span style={{ marginLeft: 10, fontSize: 9, background: "var(--warn)", color: "#000", padding: "2px 8px", borderRadius: 10, verticalAlign: "middle", fontWeight: 700, letterSpacing: "0.08em" }}>NOW</span>}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, fontSize: 14, fontFamily: "var(--font-mono)", color: "var(--text)" }}>
                    {y.total || "—"}
                    <div style={{ color: y.budget > 60000 ? "var(--danger)" : y.budget > 30000 ? "var(--warn)" : "var(--muted)", fontSize: 11, marginTop: 3 }}>{y.budget ? fmt(y.budget) : "—"}</div>
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, fontSize: 14, fontFamily: "var(--font-mono)", color: y.reviewedBudget ? "var(--success)" : "var(--muted)" }}>
                    {y.reviewedBudget ? y.reviewedTotal : "—"}
                    <div style={{ fontSize: 11, marginTop: 3 }}>{y.reviewedBudget ? fmt(y.reviewedBudget) : "awaiting finalise"}</div>
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 650, fontSize: 13, fontFamily: "var(--font-mono)", color: diff == null ? "var(--muted)" : diff > 0 ? "var(--warn)" : diff < 0 ? "var(--success)" : "var(--muted)" }}>
                    {diff == null ? "—" : diff === 0 ? "No change" : fmt(Math.abs(diff))}
                    {diff != null && diff !== 0 && <div style={{ fontSize: 11, marginTop: 3 }}>{diff > 0 ? "above projection" : "below projection"}</div>}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
                    {y.reviewedCampusData.map(c => (
                      <div key={c.campus} style={{ color: c.campus === "CCK" ? "var(--cck)" : "var(--ccw)" }}>
                        {c.campus} {c.finalized ? `${c.reviewed}/${c.projected}` : `—/${c.projected}`}
                      </div>
                    ))}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, fontSize: 13, color: statusColor, whiteSpace: "nowrap" }}>{y.status}</td>
                </tr>
              );
            })}
            <tr style={{ background: "rgba(255,255,255,0.04)", borderTop: "2px solid var(--border2)" }}>
              <td style={{ padding: "12px 16px", fontWeight: 700, fontSize: 14 }}>Total</td>
              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, fontSize: 14, fontFamily: "var(--font-mono)" }}>{totals.total}<div style={{ color: "var(--warn)", fontSize: 11, marginTop: 3 }}>{fmt(totals.budget)}</div></td>
              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, fontSize: 14, fontFamily: "var(--font-mono)", color: totals.reviewedBudget ? "var(--success)" : "var(--muted)" }}>{totals.reviewedTotal || "—"}<div style={{ fontSize: 11, marginTop: 3 }}>{totals.reviewedBudget ? fmt(totals.reviewedBudget) : "awaiting finalise"}</div></td>
              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, fontSize: 14, fontFamily: "var(--font-mono)", color: "var(--muted)" }}>—</td>
              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, fontSize: 13, color: "var(--muted)" }}>{campusFilter === "All" ? `${totals.cck} CCK · ${totals.ccw} CCW` : campusFilter}</td>
              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, fontSize: 13, color: "var(--accent)" }}>Summary</td>
            </tr>
          </tbody>
        </table>
        <div style={{ padding: "10px 16px", fontSize: 11, color: "var(--muted)", borderTop: "1px solid var(--border)" }}>
          The completed 2026 refresh is excluded from forward planning. Cost basis: {fmt(devicePrice ?? 1704)} per staff device.
        </div>
      </div>
    </div>
  );
}
